import Container from "~/components/atoms/Container";
import { useState } from "react";
import { Link, useLocation } from "@remix-run/react";
import Cookies from "js-cookie";
import { Dialog, DialogContent, DialogHeader, DialogTrigger } from "~/components/ui/dialog";
import EnquireNowForm from "../common/EnquireNowForm";
import { DEFAULT_COUNTRY } from "~/lib/constants/generalConstants";
import { EnquireNowDrawerContent } from "../common/EnquireNowDrawerContent";
import { Phone } from "lucide-react";
import { AvatarStack } from "./avatarStack";
import { getFormattedCountryName } from "~/lib/helpers/constants";
import { OhButton } from "~/components/ui/custom-radix-components/button";
import { RegionEmail } from "~/helpers/RegionEmail";



import { useCurrentRegion } from "~/lib/helpers/CurrencyHelper";
import { PAGE_SECTION_IDS, PAGE_SECTION_SCROLL_CLASS } from "~/lib/constants/pageSectionIds";

interface OfficeSearchBannerProps {
  className?: string;
  country: string
  isCampaignPage?: boolean
  description?: string
}

export default function OfficeSearchBanner({
  className,
  country = "au",
  isCampaignPage = false,
  description = ""
}: OfficeSearchBannerProps) {
  const location = useLocation();
  let { pathname } = location;
  const [isEnquireNowOpen, setIsEnquireNowOpen] = useState(false);
  const isClient = typeof window !== "undefined";
  const screenWidth = isClient ? parseInt(Cookies.get("screenWidth") || "0", 10) : 1024;
  const isMobile = screenWidth > 0 && screenWidth < 768;
  const regionConfig = useCurrentRegion();

  return (
    <div id={PAGE_SECTION_IDS.officeSearchBanner} className={`w-full min-h-[297px] h-auto relative ${PAGE_SECTION_SCROLL_CLASS}`}>
      <div className="w-full h-full bg-[linear-gradient(to_right,#B3DDE2_100%,#B3DDE2_100%)] md:bg-[linear-gradient(to_right,#B3DDE2_50%,#B3DDE2_50%_75%,#274754_75%_100%)]
        custom-w3:bg-[linear-gradient(to_right,#B3DDE2_50%,#B3DDE2_50%_60%,#274754_60%_100%)]">
        <Container htmlFor="ultra-wide">
          <div className="flex justify-center">
            <div className="w-full min-h-[297px] grid grid-cols-1 md:grid-cols-[65%_35%] h-auto overflow-hidden">
              <div className="bg-[#B3DDE2] w-full h-full">
                <div className='flex flex-col justify-center items-center text-center md:items-start md:text-left mb-5 md:mb-0 space-y-4 py-8 px-4 md:p-10 lg:p-12'>
                  <span className="text-[26px] md:text-4xl font-bold leading-tight text-oh-muted-blue lg:text-4xl">
                    {isCampaignPage ? "Still deciding?" : <>We are {getFormattedCountryName(regionConfig?.region)}'s No. 1 marketplace for serviced, coworking, and shared office space.</>}
                  </span>
                  {isCampaignPage && <h3 className="text-[26px] md:text-4xl font-bold leading-tight   text-oh-muted-blue lg:text-4xl">Get expert help — it’s <span className="text-base-red">FREE</span></h3>}
                  <p className="text-lg font-semibold text-oh-muted-blue">
                    {description ? description : `Connect with our local experts for flexible workspace solutions across ${getFormattedCountryName(regionConfig?.region)}. Get personalized recommendations, arrange tours, and secure the best deals — all at no cost.`}
                  </p>
                  {isMobile ? (
                    <>
                      <OhButton
                        variant="secondary"
                        className=' px-8  py-6 h-[44px] w-[193px] text-base font-medium'
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsEnquireNowOpen(true);
                        }}
                      >
                        Enquire Now
                      </OhButton>

                      {isEnquireNowOpen && (
                        <EnquireNowDrawerContent
                          // listing={data}
                          toggleEnquireModal={() => setIsEnquireNowOpen(false)}
                          isEnquireNowOpen={isEnquireNowOpen}
                          region={regionConfig?.region}
                          path={pathname}
                        />
                      )}
                    </>
                  ) : (
                    <Dialog>
                      <DialogTrigger asChild>
                        <OhButton
                          variant="secondary"
                          className='px-8 py-6 h-[44px] w-[193px] text-base font-medium'
                          onClick={(e) => e.stopPropagation()}
                        >
                          Enquire Now
                        </OhButton>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <DialogHeader className="font-semibold border-b pb-4">
                          Start Your Office Search
                        </DialogHeader>
                        <EnquireNowForm
                          canonicalCountry={regionConfig?.country}
                          region={country}
                          path={pathname}
                          className="w-full"
                        />
                      </DialogContent>
                    </Dialog>
                  )}

                </div>

              </div>
              <div className="w-[600px] custom-w2:w-[800px] md:w-[48%] bg- relative md:absolute md:right-0 h-[200px] md:h-full
                  rounded-tl-[50%] rounded-tr-[50%] md:rounded-none  left-1/2 -translate-x-1/2
                  md:translate-x-0 md:translate-y-0 md:top-auto md:left-auto
                  [clip-path:none] md:[clip-path:ellipse(80%_120%_at_100%_50%)] 
                  flex items-center justify-center bg-oh-muted-blue">
                <div className='flex flex-col  items-center text-center py-5 md:pt-15 pl-0 md:pl-[100px]  text-white'>
                  <div className="flex justify-center md:justify-start ">
                    <AvatarStack
                      images={[
                        `https://ik.imagekit.io/officehub/tr:h-480,fo-auto,f-avif/${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/avatars/expert-avatar-1.png`,
                        `https://ik.imagekit.io/officehub/tr:h-480,fo-auto,f-avif/${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/avatars/expert-avatar-2.png`,
                        `https://ik.imagekit.io/officehub/tr:h-480,fo-auto,f-avif/${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/avatars/expert-avatar-3.png`,
                        `https://ik.imagekit.io/officehub/tr:h-480,fo-auto,f-avif/${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/avatars/expert-avatar-4.png`,
                      ]}
                      alts={[
                        "Stacey Banks, Senior Account Manager at Office Hub",
                        "Andrew Back, Vice President of Sales at Office Hub",
                        "Sarah Goldman, Director of Operations at Office Hub",
                        "Miles Anderson, Senior Account Director at Office Hub",
                      ]}
                      titles={[
                        "Stacey Banks", "Andrew Back", "Sarah Goldman", "Miles Anderson"
                      ]}
                      size={70}
                      overlap={0.3}
                      borderWidth={3}
                    />
                  </div>

                  {regionConfig?.phoneNumber ? (
                    <>
                      <p className="mt-4 text-md font-medium flex items-center gap-2">
                        <Phone size={20} className="text-white" />
                        <span>Talk to our Experts directly</span>
                      </p>

                      <Link to={`tel:${regionConfig?.phoneNumberWithCode}`} prefetch="intent">
                        <span className="mt-1 text-2xl font-bold lg:text-3xl text-[#66E5E9]">
                          {regionConfig?.phoneNumber}
                        </span>
                      </Link>
                    </>
                  ) : <RegionEmail title="Book an Appointment" calenderUrl={true} email={false} className="mt-2 text-3xl font-bold text-[#66E5E9]" />}
                </div>
              </div>
            </div>
          </div>
        </Container>
      </div>
    </div>
  );
}
